import { Fragment, useEffect, useRef, useState } from 'react'
import { Send } from 'lucide-react'

import { buildLabel } from '@/lib/buildInfo'
import { getRuntimeEnv } from '@/lib/runtimeEnv'
import type { FeedbackConversation, FeedbackMessage } from '@/lib/feedback'

export type { FeedbackConversation, FeedbackMessage } from '@/lib/feedback'

export interface AboutScreenProps {
  conversation?: FeedbackConversation
  messages: FeedbackMessage[]
  /**
   * Read watermark captured when the screen was opened: team replies created
   * after this moment are highlighted as new. null means nothing was read
   * before; undefined disables the highlight.
   */
  newSince?: string | null
  isLoading?: boolean
  isSending?: boolean
  error?: string
  onSendMessage: (message: string) => void
}

type ChangelogEntry = {
  version: string
  changelog: string[]
}

type ChangelogData = {
  previousVersion?: string
  versions?: ChangelogEntry[]
}

function formatMessageTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } else if (diffDays === 1) {
    return 'Yesterday'
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' })
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }
}

function MessageBubble(props: { message: FeedbackMessage }) {
  const { message } = props
  const isUser = message.authorType === 'external'

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-4 py-2 ${
          isUser
            ? 'bg-blue-600 text-white'
            : 'bg-slate-200 text-slate-900'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap">{message.body}</p>
        <p
          className={`mt-1 text-[10px] ${
            isUser ? 'text-blue-200' : 'text-slate-500'
          }`}
        >
          {formatMessageTime(message.created)}
        </p>
      </div>
    </div>
  )
}

function isNewReply(message: FeedbackMessage, newSince?: string | null): boolean {
  if (newSince === undefined) return false
  return (
    message.authorType === 'admin' &&
    (newSince === null || message.created > newSince)
  )
}

export function AboutScreen(props: AboutScreenProps) {
  const { messages, newSince, isLoading, isSending, error, onSendMessage } = props
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null

  const newCount = messages.filter((m) => isNewReply(m, newSince)).length
  const firstNewId = messages.find((m) => isNewReply(m, newSince))?.id

  // Keep the newest message in view as replies arrive live.
  useEffect(() => {
    if (lastMessageId) {
      messagesEndRef.current?.scrollIntoView({ block: 'nearest' })
    }
  }, [lastMessageId])
  const [changelog, setChangelog] = useState<ChangelogData | null>(null)
  const [changelogError, setChangelogError] = useState<string | null>(null)
  const roadmapUrl = getRuntimeEnv().VITE_JOBCARDS_ROADMAP_URL?.trim() || ''
  const roadmapLabel =
    roadmapUrl.length > 40 ? `${roadmapUrl.slice(0, 37)}...` : roadmapUrl

  useEffect(() => {
    let isMounted = true
    const loadChangelog = async () => {
      try {
        const response = await fetch('/CHANGELOG.json', { cache: 'no-store' })
        if (!response.ok) {
          throw new Error(`Failed to load changelog (${response.status})`)
        }
        const data = (await response.json()) as ChangelogData
        if (isMounted) {
          setChangelog(data)
        }
      } catch (err) {
        if (isMounted) {
          setChangelogError(
            err instanceof Error ? err.message : 'Failed to load changelog',
          )
        }
      }
    }

    loadChangelog()

    return () => {
      isMounted = false
    }
  }, [])

  const versionLabel = changelog?.previousVersion?.trim()
    ? changelog.previousVersion.trim()
    : changelog
      ? 'version unknown'
      : 'Loading...'

  const changelogEntries = Array.isArray(changelog?.versions)
    ? changelog.versions
        .map((entry) => {
          if (!entry || typeof entry !== 'object') return null
          const version =
            typeof (entry as { version?: unknown }).version === 'string'
              ? (entry as { version?: string }).version?.trim()
              : ''
          const rawChangelog = (entry as { changelog?: unknown }).changelog
          const items = Array.isArray(rawChangelog)
            ? rawChangelog.filter((item): item is string => typeof item === 'string')
            : []
          if (!version || items.length === 0) return null
          return { version, changelog: items }
        })
        .filter((entry): entry is ChangelogEntry => Boolean(entry))
    : []

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    const message = String(formData.get('message') ?? '').trim()
    if (!message) return

    onSendMessage(message)
    event.currentTarget.reset()
    inputRef.current?.focus()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      const form = event.currentTarget.form
      if (form) {
        form.requestSubmit()
      }
    }
  }

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="mx-auto grid w-full max-w-container gap-6 px-4 py-6 lg:grid-cols-[1fr_360px]">
        <header className="space-y-1 lg:col-span-2">
          <h1 className="text-2xl font-semibold">About</h1>
          <p className="text-sm text-slate-600">
            Learn about FlagFinder . JobCard, talk to the team, and keep an
            eye on what's coming next.
          </p>
        </header>

        <section className="rounded-md bg-white p-4 shadow-sm">
          <div className="space-y-2">
            <div>
              <h2 className="text-lg font-semibold">FlagFinder . JobCard</h2>
              <p className="text-sm text-slate-600">
                A focused workspace for managing bookings, callouts, and the
                handoff to invoicing.
              </p>
            </div>
            <div className="flex flex-col gap-2 text-sm text-slate-600">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Version</span>
                <span className="font-medium text-slate-900">
                  {versionLabel}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Build (git)</span>
                <span className="font-medium text-slate-900">{buildLabel}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Roadmap</span>
                {roadmapUrl ? (
                  <a
                    className="font-medium text-blue-600 hover:text-blue-700"
                    href={roadmapUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={roadmapUrl}
                  >
                    {roadmapLabel}
                  </a>
                ) : (
                  <span className="text-slate-500">TBD</span>
                )}
              </div>
            </div>
            <div className="pt-3">
              <details className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <summary className="cursor-pointer text-sm font-medium text-slate-700">
                  Changelog
                </summary>
                <div className="mt-3 space-y-3 text-sm text-slate-600">
                  {changelogError ? (
                    <p className="text-amber-600">{changelogError}</p>
                  ) : changelogEntries.length === 0 ? (
                    <p className="text-slate-500">
                      {changelog ? 'No changelog yet' : 'Loading...'}
                    </p>
                  ) : (
                    changelogEntries.map((entry) => (
                      <div key={entry?.version} className="space-y-2">
                        <div className="font-semibold text-slate-800">
                          {entry?.version}
                        </div>
                        <ul className="list-disc space-y-1 pl-5">
                          {entry?.changelog.map((line) => (
                            <li key={line} className="text-slate-600">
                              {line}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))
                  )}
                </div>
              </details>
            </div>
          </div>
        </section>

        <aside className="flex flex-col rounded-md bg-white shadow-sm overflow-hidden">
          <div className="border-b border-slate-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-slate-900">Talk to the team</h2>
              {newCount > 0 ? (
                <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                  {newCount} new
                </span>
              ) : null}
            </div>
            <p className="text-xs text-slate-500">
              Send feedback, report issues, or ask questions.
            </p>
          </div>
          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 min-h-[400px] max-h-[60vh]">
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-slate-500">Loading conversation...</p>
              </div>
            ) : error ? (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <p className="text-sm text-slate-500">No messages yet</p>
                  <p className="mt-1 text-xs text-slate-400">
                    Start a conversation by sending a message below
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {messages.map((message) => (
                  <Fragment key={message.id}>
                    {message.id === firstNewId ? (
                      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-red-500">
                        <span className="h-px flex-1 bg-red-200" />
                        New
                        <span className="h-px flex-1 bg-red-200" />
                      </div>
                    ) : null}
                    <MessageBubble message={message} />
                  </Fragment>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="border-t border-slate-200 p-4">
            <form onSubmit={handleSubmit} className="flex gap-2">
              <textarea
                ref={inputRef}
                name="message"
                className="flex-1 resize-none rounded-md border border-slate-300 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
                rows={2}
                disabled={isSending}
                onKeyDown={handleKeyDown}
              />
              <button
                type="submit"
                disabled={isSending}
                className="self-end rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <span className="flex items-center gap-1">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  </span>
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </form>
          </div>
        </aside>
      </main>
    </div>
  )
}
